import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, ClipboardList, ChevronRight, AlertCircle, Star } from 'lucide-react';
import { useDealerAuth } from '../DealerAuthContext';
import { dealerGetOrders, dealerGetProducts } from '../../services/api';

const ORDER_STATUS_COLORS = {
  pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  confirmed: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  in_production: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  shipped: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  delivered: 'text-green-400 bg-green-500/10 border-green-500/20',
  cancelled: 'text-red-400 bg-red-500/10 border-red-500/20',
};

export default function DealerDashboard() {
  const { dealer, token } = useDealerAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([dealerGetOrders(token), dealerGetProducts(token)])
      .then(([o, p]) => {
        setOrders(Array.isArray(o) ? o : []);
        setProducts(Array.isArray(p) ? p.slice(0, 6) : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const pendingOrders = (orders || []).filter(o => ['new','payment_pending'].includes(o.status)).length;
  const totalSpend = (orders || []).reduce((s, o) => s + (o.subtotal || 0), 0);

  return (
    <div data-testid="dealer-dashboard">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="font-bebas text-4xl text-white tracking-wider">
          WELCOME, {dealer?.first_name?.toUpperCase() || 'DEALER'}
        </h1>
        <p className="font-inter text-sm text-zinc-500 mt-1">{dealer?.business_name}</p>
      </div>

      {/* NFA reminder */}
      <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/20 p-3 mb-6">
        <AlertCircle size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
        <p className="font-inter text-xs text-zinc-400">
          <strong className="text-orange-400">NFA Reminder:</strong> Suppressor orders require transfer through your FFL. Ensure SOT documentation is current before ordering NFA items.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Orders', value: orders.length, sub: 'All time', icon: ClipboardList, color: 'text-orange-500' },
          { label: 'Pending', value: pendingOrders, sub: 'Awaiting processing', icon: ClipboardList, color: 'text-yellow-500' },
          { label: 'Total Spend', value: `$${totalSpend.toFixed(0)}`, sub: 'At dealer pricing', icon: Package, color: 'text-green-500' },
          { label: 'Products', value: products.length, sub: 'Available to order', icon: Package, color: 'text-blue-500' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-[#1a1a1a] border border-white/10 p-5">
              <Icon size={18} className={`${s.color} mb-3`} />
              <div className={`font-bebas text-3xl tracking-wider ${s.color}`}>{s.value}</div>
              <div className="font-inter text-xs text-white mt-0.5">{s.label}</div>
              <div className="font-inter text-[11px] text-zinc-600 mt-0.5">{s.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-[#1a1a1a] border border-white/10">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h2 className="font-bebas text-lg text-white tracking-wider">RECENT ORDERS</h2>
            <Link to="/dealer/orders" className="font-inter text-xs text-orange-500 hover:text-orange-400">View all →</Link>
          </div>
          {loading ? (
            <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center">
              <p className="font-inter text-sm text-zinc-600">No orders yet</p>
              <Link to="/dealer/products" className="font-inter text-xs text-orange-500 mt-2 block">Browse products →</Link>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {orders.slice(0, 5).map(o => (
                <div key={o._id} data-testid={`order-row-${o._id}`} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="font-inter text-xs font-semibold text-white">{o.order_number}</p>
                    <p className="font-inter text-[11px] text-zinc-600">{o.items?.length} item{o.items?.length !== 1 ? 's' : ''} · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bebas text-sm text-white">${o.subtotal?.toFixed(2)}</span>
                    <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${ORDER_STATUS_COLORS[o.status] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                      {o.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="bg-[#1a1a1a] border border-white/10">
          <div className="px-5 py-4 border-b border-white/10">
            <h2 className="font-bebas text-lg text-white tracking-wider">QUICK ACTIONS</h2>
          </div>
          <div className="p-4 grid grid-cols-1 gap-3">
            {[
              { label: 'Browse & Order Products', href: '/dealer/products', icon: Package, color: 'bg-orange-500 hover:bg-orange-600' },
              { label: 'View Order History', href: '/dealer/orders', icon: ClipboardList, color: 'bg-zinc-700 hover:bg-zinc-600' },
              { label: 'My Account & Profile', href: '/dealer/profile', icon: Package, color: 'bg-zinc-700 hover:bg-zinc-600' },
            ].map(a => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.href}
                  to={a.href}
                  data-testid={`dealer-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-3 ${a.color} text-white font-inter text-sm px-4 py-3 transition-colors`}
                >
                  <Icon size={15} />
                  {a.label}
                  <ChevronRight size={14} className="ml-auto" />
                </Link>
              );
            })}
          </div>
          <div className="mx-4 mb-4 bg-[#1e1e1e] border border-white/5 p-3">
            <p className="font-inter text-xs text-zinc-500">
              <strong className="text-zinc-300">Your FFL:</strong> {dealer?.ffl_number || 'Not on file'}
            </p>
            <p className="font-inter text-xs text-zinc-500 mt-1">
              <strong className="text-zinc-300">SOT Status:</strong> {dealer?.sot_status === 'yes' ? 'SOT Holder' : 'No SOT'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
